/**
 * Compliance uplift banner — mounted on the client layout, polls
 * `/clients/{id}/compliance-status` and renders one of:
 *
 *   · state === 'warning'              → soft info banner with deadline.
 *   · state === 'remediation_required' → action-required banner with CTA
 *                                        to the spawned remediation case.
 *   · state === 'ok'                   → nothing.
 *
 * Per `client_engagement_position.md` (no dark patterns) the banner has
 * no countdown timer, no red urgency styling, and no dismiss button —
 * informational only. The actual soft-block on withdrawals / new
 * contracts lives in the relevant handlers (per
 * `kyc_validity_architecture.md` D4 — asymmetric blocking).
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import complianceStatusService, {
  type ComplianceStatus,
} from '../../services/complianceStatus.service';
import { colors } from '../../styles/theme';
import { withRetry } from '../../lib/retry';
import { readerLocale } from '../../i18n/language';
import { taxFormBannerCopy, taxFormPath } from '../../lib/taxForm';

interface BannerText {
  headline: string;
  detail: string;
}

/** Headline + detail vary by state. An expired IDENTITY document is a genuine blocking state
 *  (withdrawals + new contracts are gated) so we say so plainly — factual, not alarmist (no timers,
 *  no dismiss button; per client_engagement_position.md). A U.S. tax form stays on screen until the
 *  client signs again; if nothing changed, the form is filled in already. */
function bannerText(status: ComplianceStatus, deadlineLabel: string | null): BannerText {
  const taxForm = taxFormBannerCopy(status.reason);
  if (status.reason === 'identity_document_expired') {
    return {
      headline: 'Your identification has expired',
      detail:
        'Please upload a valid ID. Until you do, you will not be able to request '
        + 'withdrawals or sign new contracts — your existing balances and '
        + 'holdings are safe and are not affected.',
    };
  }
  if (taxForm) {
    return {
      headline: taxForm.headline,
      detail:
        deadlineLabel && taxForm.detailWithDeadline
          ? taxForm.detailWithDeadline(deadlineLabel)
          : taxForm.detail,
    };
  }
  if (status.state === 'warning') {
    return {
      headline: 'Updated documentation will be required soon',
      detail: deadlineLabel
        ? `To stay compliant we will need updated information by ${deadlineLabel}.`
        : 'To stay compliant we will need updated information from you.',
    };
  }
  return {
    headline: 'Please complete pending documentation',
    detail:
      'A short review case has been opened to refresh your file. '
      + 'Contracting new series is on hold until it is complete — your contributions '
      + 'and withdrawals are not affected.',
  };
}

interface BannerCta {
  to: string;
  label: string;
  show: boolean;
}

/** Where the CTA points: the tax form for a tax-form banner; the spawned remediation case when the
 *  daily sweep has opened one, otherwise the onboarding surface (resume) so the client can
 *  re-upload inline right away. */
function bannerCta(status: ComplianceStatus): BannerCta {
  const isIdentityExpired = status.reason === 'identity_document_expired';
  if (taxFormBannerCopy(status.reason)) {
    return { to: taxFormPath(), label: 'Review my tax form', show: true };
  }
  return {
    to: status.remediation_case_id
      ? `/client/onboarding/${status.remediation_case_id}`
      : '/client/onboarding',
    label: isIdentityExpired ? 'Update my ID' : 'Complete now',
    show: isIdentityExpired || !!status.remediation_case_id,
  };
}

export default function ComplianceUpliftBanner(): JSX.Element | null {
  const [status, setStatus] = useState<ComplianceStatus | null>(null);

  useEffect(() => {
    let cancelled = false;
    // Retried on transient failures. This banner is the only place a client
    // learns their ID has expired before they try to move money, so one network
    // blip must not hide it for the whole visit. If it still fails, the
    // withdrawal and contract gates answer `kyc_documents_not_current` with a
    // plain message at the moment it matters: that is the backstop.
    withRetry(() => complianceStatusService.getMine(), { isCancelled: () => cancelled })
      .then((st) => { if (!cancelled) setStatus(st); })
      .catch((err: unknown) => { console.warn('compliance status unavailable', err); });
    return () => { cancelled = true; };
  }, []);

  if (!status || status.state === 'ok') return null;

  const isWarning = status.state === 'warning';
  const deadlineLabel = status.deadline
    ? new Date(status.deadline).toLocaleDateString(readerLocale(), {
        year: 'numeric', month: 'long', day: 'numeric',
      })
    : null;
  const { headline, detail } = bannerText(status, deadlineLabel);
  const { to: ctaTo, label: ctaLabel, show: showCta } = bannerCta(status);

  return (
    <aside
      role="status"
      aria-live="polite"
      className="rounded-lg p-3 mb-4 flex items-start gap-3"
      style={{
        background: isWarning ? colors.infoBg : colors.warningBg,
        border: `1px solid ${isWarning ? colors.infoBorder : '#FCD34D'}`,
      }}
    >
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold mb-1"
           style={{ color: isWarning ? colors.infoText : colors.warning }}>
          {headline}
        </p>
        <p className="text-xs" style={{ color: colors.ink }}>
          {detail}
        </p>
      </div>
      {showCta && (
        <Link
          to={ctaTo}
          className="text-xs font-semibold px-3 py-1.5 rounded-md flex-shrink-0"
          style={{ background: colors.gold, color: colors.navy }}
        >
          {ctaLabel}
        </Link>
      )}
    </aside>
  );
}
